import { useState } from 'react';
import { api, ApiError, scr } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { EmptyState, ErrorNote, Field, PageHeader } from '../../ui/components';

export function StaffVoluntarySavings() {
  const { persona } = useAuth();
  const roles = persona?.roles ?? [];
  const canOperate = roles.some((r) => ['SPF_FINANCE_OFFICER', 'SPF_ADMIN'].includes(r));
  const isAdmin = roles.includes('SPF_ADMIN');

  if (!canOperate) {
    return (
      <div>
        <PageHeader title="Voluntary Savings" subtitle="Third-pillar top-up savings (P04)" />
        <ErrorNote message="Your role does not include SPF_FINANCE_OFFICER or SPF_ADMIN — this page is unavailable." />
      </div>
    );
  }

  return (
    <div>
      <PageHeader title="Voluntary Savings" subtitle="Deposits, withdrawals, standing orders & declared interest (P04)" />

      <div className="mb-6 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-xs text-amber-800">
        P04 has no back-office list of pending withdrawal requests — approve one below by pasting in its request ID
        (from the member's withdrawal acknowledgement).
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <DepositOnBehalf />
        <WithdrawOnBehalf />
      </div>

      <div className="mt-6">
        <ApproveWithdrawal />
      </div>

      <div className="mt-6 grid gap-6 lg:grid-cols-2">
        <StandingOrderOnBehalf />
        {isAdmin ? <DeclareInterest /> : <EmptyState title="Declaring interest requires SPF_ADMIN" />}
      </div>

      <div className="mt-6">
        <ApplyInterest />
      </div>
    </div>
  );
}

function DepositOnBehalf() {
  const [nin, setNin] = useState('');
  const [amount, setAmount] = useState('');
  const [ref, setRef] = useState('');
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      const r = await api.post<{ accountId: string; balance: number }>('/api/v1/voluntary/deposits', {
        nin,
        amount: Number.parseFloat(amount),
        ref: ref || `OFFICER-${Date.now()}`,
      });
      setMessage(`Deposited. New balance: ${scr(r.balance)}.`);
      setAmount('');
      setRef('');
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Deposit failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card p-4">
      <h2 className="mb-1 text-sm font-bold text-slate-700">Post a deposit on behalf of a member</h2>
      <p className="mb-3 text-xs text-slate-500">e.g. reconciling a bank transfer or payroll-deduction batch.</p>
      {error ? <ErrorNote message={error} /> : null}
      {message ? <div className="mb-3 rounded-lg bg-emerald-50 px-3.5 py-2.5 text-sm text-emerald-700">{message}</div> : null}
      <div className="space-y-3">
        <Field label="Member NIN">
          <input className="field-input" value={nin} onChange={(e) => setNin(e.target.value)} />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Amount (SCR)">
            <input className="field-input" type="number" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} />
          </Field>
          <Field label="Payment reference">
            <input className="field-input" value={ref} onChange={(e) => setRef(e.target.value)} />
          </Field>
        </div>
      </div>
      <button className="btn-primary mt-3" onClick={submit} disabled={busy || !nin || !amount}>
        {busy ? 'Depositing…' : 'Post deposit'}
      </button>
    </div>
  );
}

function WithdrawOnBehalf() {
  const [nin, setNin] = useState('');
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      const r = await api.post<{ id: string; status: string }>(`/api/v1/voluntary/accounts/${nin}/withdrawals`, {
        amount: Number.parseFloat(amount),
        reason,
      });
      setMessage(`Withdrawal request ${r.id} created (${r.status}). Approve it below once ready.`);
      setAmount('');
      setReason('');
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Withdrawal request failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card p-4">
      <h2 className="mb-1 text-sm font-bold text-slate-700">Request a withdrawal on behalf of a member</h2>
      <p className="mb-3 text-xs text-slate-500">e.g. the member requested it in person or by phone.</p>
      {error ? <ErrorNote message={error} /> : null}
      {message ? <div className="mb-3 rounded-lg bg-emerald-50 px-3.5 py-2.5 text-sm text-emerald-700">{message}</div> : null}
      <div className="space-y-3">
        <Field label="Member NIN">
          <input className="field-input" value={nin} onChange={(e) => setNin(e.target.value)} />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Amount (SCR)">
            <input className="field-input" type="number" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} />
          </Field>
          <Field label="Reason">
            <input className="field-input" value={reason} onChange={(e) => setReason(e.target.value)} />
          </Field>
        </div>
      </div>
      <button className="btn-primary mt-3" onClick={submit} disabled={busy || !nin || !amount || reason.length < 3}>
        {busy ? 'Submitting…' : 'Request withdrawal'}
      </button>
    </div>
  );
}

function ApproveWithdrawal() {
  const [withdrawalId, setWithdrawalId] = useState('');
  const [valueDate, setValueDate] = useState('');
  const [debtorAccountId, setDebtorAccountId] = useState('');
  const [bankCode, setBankCode] = useState('');
  const [accountNo, setAccountNo] = useState('');
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      const r = await api.post<{ status: string; batch: string }>(`/api/v1/voluntary/withdrawals/${withdrawalId}/approve`, {
        valueDate,
        debtorAccountId,
        bankCode,
        accountNo,
      });
      setMessage(`Withdrawal ${r.status} — payment batch ${r.batch}.`);
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Approval failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card p-4">
      <h2 className="mb-1 text-sm font-bold text-slate-700">Approve &amp; pay a withdrawal</h2>
      {error ? <ErrorNote message={error} /> : null}
      {message ? <div className="mb-3 rounded-lg bg-emerald-50 px-3.5 py-2.5 text-sm text-emerald-700">{message}</div> : null}
      <div className="grid grid-cols-5 gap-3">
        <Field label="Withdrawal request ID">
          <input className="field-input" value={withdrawalId} onChange={(e) => setWithdrawalId(e.target.value)} />
        </Field>
        <Field label="Value date">
          <input className="field-input" type="date" value={valueDate} onChange={(e) => setValueDate(e.target.value)} />
        </Field>
        <Field label="Debtor account ID">
          <input className="field-input" value={debtorAccountId} onChange={(e) => setDebtorAccountId(e.target.value)} placeholder="SPF sub-account id" />
        </Field>
        <Field label="Bank code">
          <input className="field-input" value={bankCode} onChange={(e) => setBankCode(e.target.value)} />
        </Field>
        <Field label="Account no.">
          <input className="field-input" value={accountNo} onChange={(e) => setAccountNo(e.target.value)} />
        </Field>
      </div>
      <button
        className="btn-primary mt-3"
        onClick={submit}
        disabled={busy || !withdrawalId || !valueDate || !debtorAccountId || !bankCode || !accountNo}
      >
        {busy ? 'Approving…' : 'Approve & pay'}
      </button>
    </div>
  );
}

function StandingOrderOnBehalf() {
  const [nin, setNin] = useState('');
  const [source, setSource] = useState('');
  const [amount, setAmount] = useState('');
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      await api.post(`/api/v1/voluntary/accounts/${nin}/standing-orders`, { source, amount: Number.parseFloat(amount), frequency: 'MONTHLY' });
      setMessage('Standing order set up.');
      setSource('');
      setAmount('');
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed to set up standing order');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card p-4">
      <h2 className="mb-1 text-sm font-bold text-slate-700">Set up a standing order on behalf of a member</h2>
      {error ? <ErrorNote message={error} /> : null}
      {message ? <div className="mb-3 rounded-lg bg-emerald-50 px-3.5 py-2.5 text-sm text-emerald-700">{message}</div> : null}
      <div className="space-y-3">
        <Field label="Member NIN">
          <input className="field-input" value={nin} onChange={(e) => setNin(e.target.value)} />
        </Field>
        <Field label="Source">
          <input className="field-input" value={source} onChange={(e) => setSource(e.target.value)} placeholder="e.g. employer payroll deduction" />
        </Field>
        <Field label="Monthly amount (SCR)">
          <input className="field-input" type="number" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} />
        </Field>
      </div>
      <button className="btn-primary mt-3" onClick={submit} disabled={busy || !nin || !source || !amount}>
        {busy ? 'Saving…' : 'Set up standing order'}
      </button>
    </div>
  );
}

function DeclareInterest() {
  const [year, setYear] = useState('');
  const [ratePercent, setRatePercent] = useState('');
  const [boardMinutesRef, setBoardMinutesRef] = useState('');
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      await api.post('/api/v1/voluntary/interest/declare', {
        year: Number.parseInt(year, 10),
        ratePercent: Number.parseFloat(ratePercent),
        boardMinutesRef,
      });
      setMessage(`Interest declared for ${year}: ${ratePercent}%.`);
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed to declare interest');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card p-4">
      <h2 className="mb-1 text-sm font-bold text-slate-700">Declare annual interest</h2>
      <p className="mb-3 text-xs text-slate-500">SPF_ADMIN only — board-approved rate, e.g. 3.75%.</p>
      {error ? <ErrorNote message={error} /> : null}
      {message ? <div className="mb-3 rounded-lg bg-emerald-50 px-3.5 py-2.5 text-sm text-emerald-700">{message}</div> : null}
      <div className="grid grid-cols-3 gap-3">
        <Field label="Year">
          <input className="field-input" type="number" value={year} onChange={(e) => setYear(e.target.value)} placeholder="2026" />
        </Field>
        <Field label="Rate %">
          <input className="field-input" type="number" step="0.01" value={ratePercent} onChange={(e) => setRatePercent(e.target.value)} placeholder="3.75" />
        </Field>
        <Field label="Board minutes ref">
          <input className="field-input" value={boardMinutesRef} onChange={(e) => setBoardMinutesRef(e.target.value)} />
        </Field>
      </div>
      <button className="btn-primary mt-3" onClick={submit} disabled={busy || !year || !ratePercent || !boardMinutesRef}>
        {busy ? 'Declaring…' : 'Declare interest'}
      </button>
    </div>
  );
}

function ApplyInterest() {
  const [year, setYear] = useState('');
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      const r = await api.post<{ year: number; ratePercent: number; accountsCredited: number }>(`/api/v1/voluntary/interest/apply/${year}`);
      setMessage(`${r.accountsCredited} account(s) credited at ${r.ratePercent}% for ${r.year}.`);
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed to apply interest');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card p-4">
      <h2 className="mb-1 text-sm font-bold text-slate-700">Apply declared interest to every account</h2>
      {error ? <ErrorNote message={error} /> : null}
      {message ? <div className="mb-3 rounded-lg bg-emerald-50 px-3.5 py-2.5 text-sm text-emerald-700">{message}</div> : null}
      <Field label="Year">
        <input className="field-input max-w-[160px]" type="number" value={year} onChange={(e) => setYear(e.target.value)} placeholder="2026" />
      </Field>
      <button className="btn-secondary mt-3" onClick={submit} disabled={busy || !year}>
        {busy ? 'Applying…' : 'Apply interest'}
      </button>
    </div>
  );
}
